import Typography from '@mui/material/Typography'
import React from 'react'

import { Input, Radio } from '@/app/component/atom/form'
import { getGroupTitle } from '@/app/component/atom/pz-metadata'
import ConditionField from '@/app/component/conditions/condition-field'
import { validateTopupAmounts } from '@/app/module/campaigns/campaign-validation-helpers'
import type { LocalTopupAction } from '@/app/module/campaigns/types'
import type { PersonalizationType, PzGroup } from '@/app/types'

export type TopupType = 'fixed' | 'dynamic'

// Choose an editor that can display the saved text; this does not validate the amounts.
export const getTopupType = (desiredAmount: string, maxAmount: string, maxAmountTemplated = ''): TopupType =>
  maxAmountTemplated ||
  [desiredAmount, maxAmount].some(
    (amount) =>
      amount && (!/^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(amount) || !Number.isFinite(Number(amount))),
  )
    ? 'dynamic'
    : 'fixed'

type TopupPayload = LocalTopupAction['topup']
type Props = {
  desiredAmountTemplated?: string | number | null
  maxAmount?: string | number | null
  maxAmountTemplated?: string | number | null
  personalizationList: PersonalizationType
  topupType: TopupType
  setDisabled?: (disabled: boolean) => void
  setTopup: (topup: TopupPayload) => void
  setTopupType: (type: TopupType) => void
}

const radioValues = [
  { label: 'Send same amount to all contacts', value: 'fixed' },
  { label: 'Send amount based on contact field', value: 'dynamic' },
]
const amountFields = [
  {
    name: 'desiredAmount',
    id: 'desired-amount',
    label: 'Desired Amount',
    info: 'Specify the amount you would like to send to each contact.',
  },
  {
    name: 'maxAmount',
    id: 'max-amount',
    label: 'Max Amount',
    info: 'Specify the maximum amount we can send to each contact.',
  },
] as const

// Shared amount form used by the topup content part and the topup action dialog.
const Topup: React.FC<Props> = ({
  desiredAmountTemplated = '',
  maxAmount = '',
  maxAmountTemplated = '',
  personalizationList,
  topupType,
  setDisabled,
  setTopup,
  setTopupType,
}) => {
  const [amounts, setAmounts] = React.useState({
    fixed: {
      desiredAmount: topupType === 'fixed' ? String(desiredAmountTemplated ?? '') : '',
      maxAmount: topupType === 'fixed' ? String(maxAmount ?? '') : '',
    },
    dynamic: {
      desiredAmount: topupType === 'dynamic' ? String(desiredAmountTemplated ?? '') : '',
      maxAmount: topupType === 'dynamic' ? String((maxAmountTemplated || maxAmount) ?? '') : '',
    },
  })
  const [touched, setTouched] = React.useState({ desiredAmount: false, maxAmount: false })
  const currentAmounts = amounts[topupType]
  const errors = validateTopupAmounts(currentAmounts.desiredAmount, currentAmounts.maxAmount)
  const invalid = !!(errors.desiredAmount || errors.maxAmount)
  const options = React.useMemo(
    () =>
      (Object.keys(personalizationList) as PzGroup[]).map((group) => ({
        title: getGroupTitle(group),
        data: (personalizationList[group] ?? []).map(({ label, value }) => ({ label, value: `{{${value}}}` })),
      })),
    [personalizationList],
  )

  React.useEffect(() => {
    setDisabled?.(invalid)
    setTopup({
      desiredAmountTemplated: currentAmounts.desiredAmount,
      ...(topupType === 'fixed' && { maxAmount: currentAmounts.maxAmount }),
      maxAmountTemplated: topupType === 'dynamic' ? currentAmounts.maxAmount : '',
    })
  }, [currentAmounts, invalid, topupType, setDisabled, setTopup])

  return (
    <form className="airtime-topup-form" onSubmit={(e) => e.preventDefault()}>
      <Radio
        error=""
        id="topup-type"
        name="topup-type"
        row
        value={topupType}
        values={radioValues}
        onChange={({ value }) => {
          setTouched({ desiredAmount: false, maxAmount: false })
          setTopupType(value as TopupType)
        }}
      />
      {amountFields.map(({ name, id, label, info }) => {
        const error = touched[name] ? errors[name] : ''
        const onChange = (value: string) =>
          setAmounts((previous) => ({
            ...previous,
            [topupType]: { ...previous[topupType], [name]: value },
          }))
        const onBlur = () => {
          setTouched((previous) => ({ ...previous, [name]: true }))
        }
        return (
          <div key={`${topupType}-${name}`} data-testid={`${id}-field`} style={{ marginTop: 10, marginBottom: 10 }}>
            {topupType === 'fixed' ? (
              <Input
                error={error}
                id={`${id}-input`}
                info={info}
                label={label}
                name={name}
                required={name === 'maxAmount'}
                type="number"
                value={currentAmounts[name]}
                variant="standard"
                onBlur={onBlur}
                onChange={({ value }) => onChange(value)}
              />
            ) : (
              <>
                <ConditionField
                  error={!!error}
                  field={currentAmounts[name]}
                  fieldError={!!error}
                  helperText={error}
                  hideRemoveSelection={false}
                  id={`${id}-select`}
                  label={label}
                  name={id}
                  options={options}
                  personalizationList={personalizationList}
                  required={name === 'maxAmount'}
                  onBlur={onBlur}
                  onChange={onChange}
                  onSelect={onChange}
                />
                <Typography color="textSecondary" variant="caption">
                  {info}
                </Typography>
              </>
            )}
          </div>
        )
      })}
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div>
          <Typography variant="caption" color="textSecondary">
            The amount sent will be in the currency specified in our{' '}
            <a
              href="https://www.engagespark.com/support/airtime-topup-transfer-api-global-pricing/"
              target="_blank"
              rel="noopener noreferrer"
            >
              pricing table
            </a>
            , where you can find the available amounts per country and telco.
            <br />
          </Typography>
        </div>
        <div>
          <Typography variant="caption" color="textSecondary">
            Because the denominations that we can send vary by country and telco, we ask you for a{' '}
            <strong>desired amount</strong> and a <strong>maximum amount</strong>. We will send the{' '}
            <strong>desired amount</strong> if it's available for the contact. If it's not available, we'll send the
            lowest amount available above the <strong>desired amount</strong>, but no higher than the{' '}
            <strong>maximum amount</strong> you set. <br />
            If you do not specify a <strong>desired amount</strong>, we will send the <strong>maximum amount</strong>{' '}
            you specify if it's available for the contact. If it's not available, we'll send the next lowest amount
            available.
          </Typography>
        </div>
      </div>
    </form>
  )
}

export default Topup
